import { useEffect, useMemo, useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    InputAdornment,
    Paper,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CheckIcon from '@mui/icons-material/Check';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded';
import GroupAddRoundedIcon from '@mui/icons-material/GroupAddRounded';
import ManageAccountsRoundedIcon from '@mui/icons-material/ManageAccountsRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
import Swal from 'sweetalert2';
import { t } from 'i18next';
import { SCUIGeneralDrawerHeader } from '../../../../atomic/molecules';
import { OperatorCommand } from '../../../../services/command';
import { OperatorQuery } from '../../../../services/query';
import TimeHelper from '../../../../utils/time';
import { LUR } from '../listUserRoles.classes';
import SelectOperatorModal from './components/SelectOperatorModal';
import { RoleModal } from './components/RoleModal';
import UpdateUserInfoComponent from './UpdateUserInfoComponent';

const accordionSx = {
    mb: 2,
    borderRadius: '12px !important',
    overflow: 'hidden',
    border: '1px solid #E2E8F0',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    '&:before': { display: 'none' },
};

const accordionSummarySx = {
    bgcolor: '#F8FAFC',
    borderBottom: '1px solid #E2E8F0',
    minHeight: 64,
    '&:hover': {
        bgcolor: '#F1F5F9',
    },
    '& .MuiAccordionSummary-content': {
        my: 1.5,
    },
};

const actionButtonSx = {
    borderRadius: '8px',
    fontWeight: 600,
    fontSize: '0.8125rem',
    px: 2.5,
    py: 0.75,
    minWidth: 96,
    flexShrink: 0,
    textTransform: 'none' as const,
    color: '#fff',
    boxShadow: 'none',
    transition: 'all 0.2s ease',
    '&:hover': {
        boxShadow: '0 4px 14px rgba(37, 99, 235, 0.25)',
        transform: 'translateY(-1px)',
    },
};

const actionCardSx = {
    p: 2,
    height: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'stretch',
    gap: 1.5,
    border: '1px solid #E5E7EB',
    backgroundColor: '#FAFAFA',
};

const infoLabelSx = {
    fontWeight: 600,
    fontSize: '0.8rem',
    color: '#6B7280',
    minWidth: 160,
};

const infoValueSx = {
    fontWeight: 500,
    fontSize: '0.875rem',
    color: '#111827',
};

const ListUserDetailComponent = ({ selectedUser, refresh, onClose }: any) => {
    const [roleList, setRoleList] = useState<any>();
    const harmonyLoading = false;
    const operatorIdUpdateLoading = false;
    const [operatorIdUpdateCompleted, setOperatorIdUpdateCompleted] = useState(false);
    const [completed, setCompleted] = useState(false);
    const [transferModalOpen, setTransferModalOpen] = useState(false);
    const [denemeModalOpen, setDenemeModalOpen] = useState(false);
    const [roleFilter, setRoleFilter] = useState('');

    const filteredRoles = useMemo(() => {
        if (!roleList) return [];
        if (!roleFilter.trim()) return roleList;
        const query = roleFilter.trim().toLowerCase();
        return roleList.filter((role: any) => role.roleNameKey?.toLowerCase().includes(query));
    }, [roleList, roleFilter]);

    const handleClick = async () => {
        try {
            const params: any = {};
            params.applicationUserId = selectedUser.applicationUserId;
            params.userRef = selectedUser.userRef;
            await OperatorCommand.UpdateHarmonyData(params).then(() => {
                Swal.fire({
                    icon: 'success',
                    title: t('general.success'),
                    text: t('general.userInfoUpdated'),
                    showConfirmButton: false,
                    timer: 1500,
                });
            });
        } catch (error) {
            console.log('Hata :>> ', error);
        } finally {
            setCompleted(true);
            setTimeout(() => {
                refresh();
            }, 1500);
        }
    };

    const handleUpdateOperatorIdClick = async () => {
        try {
            const params: any = {};
            params.applicationUserId = selectedUser.applicationUserId;
            params.userRef = selectedUser.userRef;
            params.isActive = selectedUser.isActive;
            await OperatorCommand.UpdateOperatorIdData(params).then((res) => {
                if (!res?.error) {
                    Swal.fire({
                        icon: 'success',
                        title: t('general.success'),
                        text: t('general.userInfoUpdated'),
                        showConfirmButton: false,
                        timer: 1500,
                    });
                } else {
                    Swal.fire({
                        icon: 'error',
                        title: res.error,
                        showConfirmButton: false,
                        timer: 1500,
                    });
                }
            });
        } catch (error) {
            console.log('Hata :>> ', error);
        } finally {
            setOperatorIdUpdateCompleted(true);
            setTimeout(() => {
                refresh();
            }, 1500);
        }
    };

    const handleTransferRoleClick = async () => {
        setTransferModalOpen(true);
    };

    const handleTransferRoleClose = async () => {
        setTransferModalOpen(false);
    };

    const handleDenemeRoleClick = async () => {
        setDenemeModalOpen(true);
    };

    const handleDenemeRoleClose = async () => {
        setDenemeModalOpen(false);
    };

    useEffect(() => {
        GetOperatorRolesList();
    }, []);

    const GetOperatorRolesList: any = async () => {
        try {
            const operator: any = await OperatorQuery.GetOperatorRolesList(selectedUser.userRef);
            setRoleList(operator.model && operator.model.data);
        } catch (error) {
            console.log('Hata :>> ', error);
        }
    };

    function deleteOperator(row: any, role: any): void {
        console.log(role);
        Swal.fire({
            text: t('general.sureText') + ' ' + row.username,
            cancelButtonText: t('general.cancel'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: t('general.removeUser'),
        }).then((result: any) => {
            if (result.isConfirmed) {
                const params: any = {};
                params.applicationUserId = Number(row.applicationUserId);
                params.roleId = Number(role.roleId);

                OperatorCommand.DeleteOperatorFromRole(params)
                    .then((res: any) => {
                        Swal.fire(res.message, t('general.deleted'), 'success');
                        GetOperatorRolesList();
                    })
                    .catch((error: any) => {
                        Swal.fire('', t('general.couldntDeleted'), 'error');
                        console.log('Hata :>> ', error);
                    })
                    .finally(() => {});
            }
        });
    }

    const quickActions = [
        {
            key: 'harmony',
            label: t('general.updateFromHarmony') as string,
            icon: <SyncRoundedIcon sx={{ color: '#2563eb' }} />,
            className: LUR.btnDetailHarmony,
            rowClass: LUR.detailRowHarmony,
            onClick: handleClick,
            loading: harmonyLoading,
            completed: completed,
            text: t('general.update') as string,
        },
        {
            key: 'operatorId',
            label: t('general.updateOperatorId') as string,
            icon: <ManageAccountsRoundedIcon sx={{ color: '#0f766e' }} />,
            className: LUR.btnDetailOperatorId,
            rowClass: LUR.detailRowOperatorId,
            onClick: handleUpdateOperatorIdClick,
            loading: operatorIdUpdateLoading,
            completed: operatorIdUpdateCompleted,
            text: t('general.update') as string,
        },
        {
            key: 'transfer',
            label: t('general.UserAddRoleTransfer') as string,
            icon: <SwapHorizRoundedIcon sx={{ color: '#ea580c' }} />,
            className: LUR.btnDetailTransfer,
            rowClass: LUR.detailRowTransfer,
            onClick: handleTransferRoleClick,
            loading: false,
            completed: false,
            text: t('general.transfer') as string,
        },
        {
            key: 'roleGroup',
            label: t('general.UserAddRoleGroup') as string,
            icon: <GroupAddRoundedIcon sx={{ color: '#7c3aed' }} />,
            className: LUR.btnDetailRoleGroupAdd,
            rowClass: LUR.detailRowRoleGroup,
            onClick: handleDenemeRoleClick,
            loading: false,
            completed: false,
            text: t('general.add') as string,
        },
    ];

    const infoRows = [
        {
            key: 'storeCode',
            label: t('general.storeCode'),
            value: selectedUser?.storeCode,
            className: LUR.detailRowStoreCode,
            dataAttr: { 'data-store-code': selectedUser?.storeCode },
        },
        {
            key: 'email',
            label: t('general.eMail'),
            value: selectedUser?.email,
            className: LUR.detailRowEmail,
            dataAttr: { 'data-email': selectedUser?.email },
        },
        {
            key: 'operatorId',
            label: t('general.operatorId'),
            value: selectedUser?.operatorId,
            className: LUR.detailRowOperatorIdInfo,
            dataAttr: { 'data-operator-id': selectedUser?.operatorId },
        },
        {
            key: 'userRef',
            label: 'UserRef',
            value: selectedUser?.userRef,
            className: LUR.detailRowUserRef,
            dataAttr: { 'data-user-ref': selectedUser?.userRef },
        },
        {
            key: 'hrref',
            label: 'Hrref',
            value: selectedUser?.hrref,
            className: LUR.detailRowHrref,
            dataAttr: { 'data-hrref': selectedUser?.hrref },
        },
        {
            key: 'domain',
            label: t('general.domainName'),
            value: selectedUser?.domainName,
            className: LUR.detailRowDomain,
            dataAttr: { 'data-domain-name': selectedUser?.domainName },
        },
        {
            key: 'harmonySyncDate',
            label: t('general.harmonySyncDate'),
            value: selectedUser?.harmonySyncDate ? TimeHelper.formatDateHour(selectedUser.harmonySyncDate) || '-' : '-',
            className: LUR.detailRowHarmonySyncDate,
        },
        {
            key: 'createDate',
            label: t('mobileApp.createDate'),
            value: selectedUser?.createDate ? TimeHelper.formatDateHour(selectedUser.createDate) || '-' : '-',
            className: LUR.detailRowCreateDate,
        },
        {
            key: 'updateDate',
            label: t('mobileApp.updateDate'),
            value: selectedUser?.updateDate ? TimeHelper.formatDateHour(selectedUser.updateDate) || '-' : '-',
            className: LUR.detailRowUpdateDate,
        },
    ];

    return (
        <Box
            sx={{ p: 3 }}
            className={LUR.detailRoot}
            data-user-ref={selectedUser?.userRef}
            data-application-user-id={selectedUser?.applicationUserId}
        >
            <Dialog
                open={transferModalOpen}
                onClose={handleTransferRoleClose}
                className={LUR.detailDialogTransfer}
                maxWidth="sm"
                fullWidth
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: '16px',
                            boxShadow: '0 8px 40px rgba(15, 23, 42, 0.15)',
                        },
                    },
                }}
            >
                <DialogTitle
                    className={LUR.detailDialogTransferTitle}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        fontWeight: 700,
                        color: '#111827',
                        borderBottom: '1px solid #E5E7EB',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SwapHorizRoundedIcon sx={{ color: '#2563eb' }} />
                        {t('authorizationManagement.selectUser') as string}
                    </Box>
                    <IconButton size="small" onClick={handleTransferRoleClose} aria-label="close">
                        <CloseRoundedIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    <SelectOperatorModal selectedUser={selectedUser} />
                </DialogContent>
            </Dialog>

            <Dialog
                open={denemeModalOpen}
                onClose={handleDenemeRoleClose}
                className={LUR.detailDialogRoleGroup}
                maxWidth="sm"
                fullWidth
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: '16px',
                            boxShadow: '0 8px 40px rgba(15, 23, 42, 0.15)',
                        },
                    },
                }}
            >
                <DialogTitle
                    className={LUR.detailDialogRoleGroupTitle}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        fontWeight: 700,
                        color: '#111827',
                        borderBottom: '1px solid #E5E7EB',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <GroupAddRoundedIcon sx={{ color: '#7c3aed' }} />
                        {t('general.AddRoleGroup') as string}
                    </Box>
                    <IconButton size="small" onClick={handleDenemeRoleClose} aria-label="close">
                        <CloseRoundedIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <RoleModal
                        applicationUserId={selectedUser.applicationUserId}
                        GetOperatorRolesList={GetOperatorRolesList}
                        roleList={roleList}
                    />
                </DialogContent>
            </Dialog>

            <Grid className={LUR.detailHeaderBox} size={{ xs: 12 }}>
                <SCUIGeneralDrawerHeader header={t('general.userEdit')} onClose={onClose} />
            </Grid>

            {roleList && selectedUser && (
                <Box sx={{ mt: 3 }}>
                    <Accordion
                        defaultExpanded
                        className={`${LUR.detailAccordion} ${LUR.detailAccordionEdit}`}
                        sx={accordionSx}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon sx={{ color: '#475569' }} />}
                            sx={accordionSummarySx}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <EditRoundedIcon sx={{ color: '#7c3aed' }} />
                                <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#111827' }}>
                                    {t('authorizationManagement.editUser') as string}
                                </Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#FAFBFC' }}>
                            <UpdateUserInfoComponent
                                hideHeader
                                selectedUser={selectedUser}
                                refresh={refresh}
                            />
                        </AccordionDetails>
                    </Accordion>

                    <Accordion
                        className={`${LUR.detailAccordion} ${LUR.detailAccordionActions}`}
                        sx={accordionSx}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon sx={{ color: '#475569' }} />}
                            sx={accordionSummarySx}
                        >
                            <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#111827' }}>
                                {t('authorizationManagement.listUser.quickActions') as string}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#FAFBFC' }}>
                            <Grid container spacing={2} className={LUR.detailActionsSection}>
                                {quickActions.map((action) => (
                                    <Grid key={action.key} size={{ xs: 12, sm: 6 }}>
                                        <Paper
                                            elevation={0}
                                            className={`${LUR.detailActionCard} ${LUR.detailRow} ${action.rowClass}`}
                                            sx={actionCardSx}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1 }}>
                                                <Box sx={{ flexShrink: 0, mt: 0.25 }}>{action.icon}</Box>
                                                <Typography
                                                    className={LUR.detailCellLabel}
                                                    sx={{
                                                        fontWeight: 600,
                                                        fontSize: '0.875rem',
                                                        color: '#374151',
                                                        lineHeight: 1.45,
                                                        wordBreak: 'break-word',
                                                    }}
                                                >
                                                    {action.label}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                <Button
                                                    className={action.className}
                                                    data-completed={action.completed}
                                                    variant="contained"
                                                    color="primary"
                                                    size="small"
                                                    disabled={action.loading || action.completed}
                                                    onClick={action.onClick}
                                                    sx={actionButtonSx}
                                                >
                                                    {action.loading ? (
                                                        <CircularProgress size={18} color="inherit" />
                                                    ) : action.completed ? (
                                                        <CheckIcon fontSize="small" />
                                                    ) : (
                                                        action.text
                                                    )}
                                                </Button>
                                            </Box>
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                        </AccordionDetails>
                    </Accordion>

                    <Accordion
                        className={`${LUR.detailAccordion} ${LUR.detailAccordionInfo}`}
                        sx={accordionSx}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon sx={{ color: '#475569' }} />}
                            sx={accordionSummarySx}
                        >
                            <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#111827' }}>
                                {t('authorizationManagement.listUser.userInformation') as string}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#FAFBFC' }}>
                            <Paper elevation={0} className={LUR.detailInfoPaper}>
                                {infoRows.map((row) => (
                                    <Box
                                        key={row.key}
                                        className={`${LUR.detailInfoRow} ${LUR.detailRow} ${row.className}`}
                                        sx={{ display: 'flex', alignItems: 'center', px: 2.5, py: 1.5, gap: 2 }}
                                    >
                                        <Typography className={LUR.detailCellLabel} sx={infoLabelSx}>
                                            {row.label as string}
                                        </Typography>
                                        <Typography className={LUR.detailCellValue} sx={infoValueSx} {...row.dataAttr}>
                                            {row.value ?? '-'}
                                        </Typography>
                                    </Box>
                                ))}
                                <Box
                                    className={`${LUR.detailInfoRow} ${LUR.detailRow} ${LUR.detailRowIsStoreEmployee}`}
                                    sx={{ display: 'flex', alignItems: 'center', px: 2.5, py: 1.5, gap: 2 }}
                                >
                                    <Typography className={LUR.detailCellLabel} sx={infoLabelSx}>
                                        {t('general.isStoreEmployee') as string}
                                    </Typography>
                                    <Box
                                        className={LUR.detailCellValue}
                                        data-is-store-employee={
                                            selectedUser.isStoreEmployee === undefined
                                                ? ''
                                                : String(selectedUser.isStoreEmployee)
                                        }
                                    >
                                        {selectedUser.isStoreEmployee !== undefined ? (
                                            selectedUser.isStoreEmployee ? (
                                                <Chip
                                                    icon={<CheckCircleOutlineIcon />}
                                                    label={t('general.yes') as string}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: '#DCFCE7',
                                                        color: '#15803D',
                                                        fontWeight: 600,
                                                        '& .MuiChip-icon': { color: '#15803D' },
                                                    }}
                                                />
                                            ) : (
                                                <Chip
                                                    icon={<CancelOutlinedIcon />}
                                                    label={t('general.no') as string}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: '#FEE2E2',
                                                        color: '#DC2626',
                                                        fontWeight: 600,
                                                        '& .MuiChip-icon': { color: '#DC2626' },
                                                    }}
                                                />
                                            )
                                        ) : (
                                            '-'
                                        )}
                                    </Box>
                                </Box>
                            </Paper>
                        </AccordionDetails>
                    </Accordion>

                    <Accordion
                        defaultExpanded
                        className={`${LUR.detailAccordion} ${LUR.detailAccordionRoles}`}
                        sx={accordionSx}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon sx={{ color: '#475569' }} />}
                            sx={accordionSummarySx}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#111827' }}>
                                    {t('authorizationManagement.listUser.assignedRoles') as string}
                                </Typography>
                                {roleList.length > 0 && (
                                    <Chip
                                        label={roleList.length}
                                        size="small"
                                        sx={{ fontWeight: 700, backgroundColor: '#EFF6FF', color: '#2563eb' }}
                                    />
                                )}
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#FAFBFC' }}>
                            <Box className={LUR.detailRolesSection}>
                                <TextField
                                    className={LUR.inputDetailRoleFilter}
                                    size="small"
                                    fullWidth
                                    placeholder={t('authorizationManagement.listUser.filterRoleName') as string}
                                    value={roleFilter}
                                    onChange={(e) => setRoleFilter(e.target.value)}
                                    slotProps={{
                                        input: {
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <FilterListRoundedIcon sx={{ color: '#9CA3AF', fontSize: 20 }} />
                                                </InputAdornment>
                                            ),
                                            endAdornment: roleFilter ? (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setRoleFilter('')}
                                                        aria-label="clear filter"
                                                    >
                                                        <CloseRoundedIcon fontSize="small" />
                                                    </IconButton>
                                                </InputAdornment>
                                            ) : null,
                                        },
                                    }}
                                    sx={{
                                        mb: 2,
                                        '& .MuiOutlinedInput-root': { borderRadius: '12px', backgroundColor: '#fff' },
                                    }}
                                />

                                {filteredRoles.length > 0 ? (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        {filteredRoles.map((role: any, index: number) => (
                                            <Paper
                                                key={role.roleId ?? index}
                                                elevation={0}
                                                className={`${LUR.detailRow} ${LUR.detailRowRole} ${LUR.detailRoleChip}`}
                                                data-role-id={role.roleId}
                                                data-role-name={role.roleNameKey}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    px: 2,
                                                    py: 1.25,
                                                    border: '1px solid #E5E7EB',
                                                    borderRadius: '12px',
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                                                    <Chip
                                                        label={`${index + 1}`}
                                                        size="small"
                                                        sx={{
                                                            minWidth: 28,
                                                            height: 24,
                                                            fontWeight: 700,
                                                            backgroundColor: '#F3F4F6',
                                                            color: '#6B7280',
                                                        }}
                                                    />
                                                    <Typography
                                                        className={LUR.detailRoleName}
                                                        data-role-id={role.roleId}
                                                        data-role-name={role.roleNameKey}
                                                        sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#111827' }}
                                                    >
                                                        {role.roleNameKey}
                                                    </Typography>
                                                </Box>
                                                <Tooltip title={t('authorizationManagement.removeRole') as string}>
                                                    <IconButton
                                                        className={LUR.btnDetailRoleDelete}
                                                        data-role-id={role.roleId}
                                                        data-role-name={role.roleNameKey}
                                                        onClick={() => deleteOperator(selectedUser, role)}
                                                        size="small"
                                                        sx={{
                                                            color: '#DC2626',
                                                            border: '1px solid #FECACA',
                                                            backgroundColor: '#FEF2F2',
                                                            borderRadius: '8px',
                                                            transition: 'all 0.15s ease',
                                                            '&:hover': {
                                                                backgroundColor: '#FEE2E2',
                                                                transform: 'scale(1.05)',
                                                            },
                                                        }}
                                                    >
                                                        <CancelIcon sx={{ fontSize: 18 }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </Paper>
                                        ))}
                                    </Box>
                                ) : (
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 3,
                                            textAlign: 'center',
                                            borderRadius: '12px',
                                            border: '1px dashed #D1D5DB',
                                            backgroundColor: '#FAFAFA',
                                        }}
                                    >
                                        <SearchRoundedIcon sx={{ color: '#9CA3AF', mb: 1 }} />
                                        <Typography variant="body2" sx={{ color: '#6B7280', fontWeight: 500 }}>
                                            {t('general.notFound') as string}
                                        </Typography>
                                    </Paper>
                                )}
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                </Box>
            )}
        </Box>
    );
};

export default ListUserDetailComponent;
